import { useTheme } from '../../../../contexts/ThemeContext'

export function ColorScheme() {
  const { gameThemeInput, resolvedTheme } = useTheme()
  const { core, derived, playerColors, providedByGame } = resolvedTheme
  const hasGame = !!gameThemeInput

  const ColorSwatch = ({ 
    label, 
    color, 
    description, 
    providedByGame: isProvided 
  }: { 
    label: string
    color: string
    description?: string
    providedByGame?: boolean
  }) => {
    const tooltipText = description ? `${color}\n${description}` : color
    return (
      <div 
        className="flex items-center gap-2"
        title={tooltipText}
      >
        <div 
          className="w-4 h-4 rounded flex-shrink-0 border"
          style={{ 
            backgroundColor: color,
            borderColor: isProvided ? 'var(--accent)' : 'transparent',
            borderWidth: isProvided ? '2px' : '0px'
          }}
        />
        <div className="text-xs flex items-center gap-1">
          {label}
          {isProvided && (
            <span className="text-[10px] text-accent" title="Provided by game">
              ●
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto p-4 space-y-3">
      {/* Section 1: Non-derived tokens */}
      <div>
        <h2 className="text-sm font-bold mb-1">Non-Derived Tokens</h2>
        <div className="text-xs text-muted mb-2">
          Core tokens that must be defined. Green dot indicates provided by game.
        </div>
        <div className="space-y-1 pl-2">
          <ColorSwatch 
            label="Accent" 
            color={core.accent}
            description="Primary accent/brand color"
            providedByGame={providedByGame.accent}
          />
          <ColorSwatch 
            label="App Background" 
            color={core.appBackground}
            description="Main app background"
            providedByGame={providedByGame.appBackground}
          />
          <ColorSwatch 
            label="Surface Background" 
            color={core.surfaceBackground}
            description="Elevated surface background"
            providedByGame={providedByGame.surfaceBackground}
          />
          <ColorSwatch 
            label="Text Primary" 
            color={core.textPrimary}
            description="Primary text color"
            providedByGame={providedByGame.textPrimary}
          />
          <ColorSwatch 
            label="Danger" 
            color={core.danger}
            description="Base danger/error color (semantic red)"
            providedByGame={providedByGame.danger}
          />
        </div>
      </div>

      {/* Section 2: Player Colors (only if in game) */}
      {hasGame && playerColors.length > 0 && (
        <div>
          <h2 className="text-sm font-bold mb-1">Player Colors</h2>
          <div className="space-y-1 pl-2">
            {playerColors.map((playerColor: { primary: string; foreground: string; background: string }, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded flex-shrink-0 border"
                  style={{ backgroundColor: playerColor.primary }}
                  title={`Primary: ${playerColor.primary}`}
                />
                <div 
                  className="w-4 h-4 rounded flex-shrink-0 border"
                  style={{ backgroundColor: playerColor.foreground }}
                  title={`Foreground: ${playerColor.foreground}`}
                />
                <div 
                  className="w-4 h-4 rounded flex-shrink-0 border"
                  style={{ backgroundColor: playerColor.background }}
                  title={`Background: ${playerColor.background}`}
                />
                <div className="text-xs">Player {index}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 3: Derived Tokens */}
      <div>
        <h2 className="text-sm font-bold mb-1">Derived Tokens</h2>
        <div className="text-xs text-muted mb-2">
          Calculated from non-derived tokens
        </div>
        
        <div className="space-y-3">
          {/* Text Colors */}
          <div>
            <div className="text-xs font-semibold mb-1 pl-2">Text Colors</div>
            <div className="space-y-1 pl-4">
              <ColorSwatch 
                label="Text Secondary" 
                color={derived.textSecondary}
                description="Secondary text (derived from textPrimary)"
              />
              <ColorSwatch 
                label="Text Muted" 
                color={derived.textMuted}
                description="Muted text (derived from textPrimary)"
              />
              <ColorSwatch 
                label="Text Inverse" 
                color={derived.textInverse}
                description="Text for dark backgrounds (derived)"
              />
            </div>
          </div>

          {/* Accent Variants */}
          <div>
            <div className="text-xs font-semibold mb-1 pl-2">Accent Variants</div>
            <div className="space-y-1 pl-4">
              <ColorSwatch 
                label="Accent Foreground" 
                color={derived.accentForeground}
                description="Text on accent backgrounds (derived from accent)"
              />
              <ColorSwatch 
                label="Accent Background" 
                color={derived.accentBackground}
                description="Accent background (derived from accent)"
              />
            </div>
          </div>

          {/* Backgrounds */}
          <div>
            <div className="text-xs font-semibold mb-1 pl-2">Backgrounds</div>
            <div className="space-y-1 pl-4">
              <ColorSwatch 
                label="Muted Background" 
                color={derived.mutedBackground}
                description="Muted background (derived)"
              />
              <ColorSwatch 
                label="Subtle Background" 
                color={derived.subtleBackground}
                description="Subtle background (derived)"
              />
            </div>
          </div>

          {/* Borders */}
          <div>
            <div className="text-xs font-semibold mb-1 pl-2">Borders</div>
            <div className="space-y-1 pl-4">
              <ColorSwatch 
                label="Border" 
                color={derived.border}
                description="General border (derived from textPrimary)"
              />
              <ColorSwatch 
                label="Divider" 
                color={derived.divider}
                description="Divider (derived from border)"
              />
              <ColorSwatch 
                label="Focus Ring" 
                color={derived.focusRing}
                description="Focus ring (uses accent)"
              />
            </div>
          </div>

          {/* Danger Variants */}
          <div>
            <div className="text-xs font-semibold mb-1 pl-2">Danger Variants</div>
            <div className="space-y-1 pl-4">
              <ColorSwatch 
                label="Danger Background" 
                color={derived.dangerBackground}
                description="Danger background (derived from danger)"
              />
              <ColorSwatch 
                label="Danger Border" 
                color={derived.dangerBorder}
                description="Danger border (derived from danger)"
              />
              <ColorSwatch 
                label="Danger Foreground" 
                color={derived.dangerForeground}
                description="Danger text (derived from danger)"
              />
            </div>
          </div>
        </div>
      </div>

      {!hasGame && (
        <div className="text-xs text-muted italic">
          Using default theme colors (no game active)
        </div>
      )}
    </div>
  )
}
